import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api'

const TOPICS = ['Sécurité incendie', 'Premiers secours', 'Habilitation électrique', 'Évacuation', 'Formation SST', 'Autre']

interface Registration {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  status: string
  createdAt: string
  company: { name: string } | null
}

interface TrainingOffer {
  id: string
  title: string
  description: string
  topic: string
  durationH: number
  price: number
  maxPax: number | null
  isRemote: boolean
  city: string | null
  status: string
  createdAt: string
  company: { name: string } | null
  dates: { id: string; date: string }[]
  _count: { registrations: number }
  registrations: Registration[]
}

function fmt(cents: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'bg-success/10 border-success/30 text-success',
  FULL: 'bg-accent/10 border-accent/30 text-accent',
  CANCELLED: 'bg-danger/10 border-danger/30 text-danger',
}
const STATUS_LABEL: Record<string, string> = { ACTIVE: 'Active', FULL: 'Complet', CANCELLED: 'Annulée' }

const REG_COLOR: Record<string, string> = {
  PENDING: 'bg-white/[0.06] border-white/[0.12] text-text-muted',
  CONFIRMED: 'bg-success/10 border-success/30 text-success',
  CANCELLED: 'bg-danger/10 border-danger/30 text-danger',
}

export default function MesFormationsPage() {
  const [offers, setOffers] = useState<TrainingOffer[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [form, setForm] = useState({
    title: '', description: '', topic: TOPICS[0], durationH: '', price: '',
    maxPax: '', isRemote: false, city: '', newDate: '',
  })
  const [dates, setDates] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function load() {
    const data = await apiFetch<TrainingOffer[]>('/catalogue/my-offers')
    setOffers(data)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  function addDate() {
    if (!form.newDate || dates.includes(form.newDate)) return
    setDates((v) => [...v, form.newDate].sort())
    setForm((v) => ({ ...v, newDate: '' }))
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      await apiFetch('/catalogue/my-offers', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          topic: form.topic,
          durationH: parseFloat(form.durationH),
          price: parseFloat(form.price),
          maxPax: form.maxPax ? parseInt(form.maxPax) : undefined,
          isRemote: form.isRemote,
          city: form.city || undefined,
          dates: dates.length ? dates : undefined,
        }),
      })
      setAdding(false)
      setForm({ title: '', description: '', topic: TOPICS[0], durationH: '', price: '', maxPax: '', isRemote: false, city: '', newDate: '' })
      setDates([])
      await load()
    } catch (e: unknown) { setError((e as Error).message) }
    finally { setSaving(false) }
  }

  async function toggleStatus(offer: TrainingOffer) {
    const newStatus = offer.status === 'ACTIVE' ? 'CANCELLED' : 'ACTIVE'
    await apiFetch(`/catalogue/my-offers/${offer.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) })
    await load()
  }

  async function addOfferDate(offerId: string, date: string) {
    if (!date) return
    await apiFetch(`/catalogue/my-offers/${offerId}/dates`, { method: 'POST', body: JSON.stringify({ date }) })
    await load()
  }

  async function removeDate(dateId: string) {
    await apiFetch(`/catalogue/my-offers/dates/${dateId}`, { method: 'DELETE' })
    await load()
  }

  async function confirmRegistration(regId: string) {
    await apiFetch(`/catalogue/my-offers/registrations/${regId}/confirm`, { method: 'POST' })
    await load()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Mes formations</h1>
          <p className="text-text-secondary text-sm mt-1">Publiez et gérez vos offres de formation dans le catalogue public.</p>
        </div>
        <button onClick={() => setAdding((v) => !v)} className="glass-btn-accent px-4 py-2.5 text-sm rounded-xl">
          {adding ? 'Annuler' : '+ Nouvelle formation'}
        </button>
      </div>

      {adding && (
        <form onSubmit={handleCreate} className="glass-card p-5 space-y-4">
          <h2 className="text-sm font-semibold text-text-primary">Nouvelle offre de formation</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="glass-label">Intitulé *</label>
              <input className="glass-input" required value={form.title} onChange={(e) => setForm((v) => ({ ...v, title: e.target.value }))} placeholder="ex. Formation SSIAP 1 — initiale" />
            </div>
            <div>
              <label className="glass-label">Thème *</label>
              <select className="glass-input" value={form.topic} onChange={(e) => setForm((v) => ({ ...v, topic: e.target.value }))}>
                {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="glass-label">Durée (heures) *</label>
              <input type="number" min="0.5" step="0.5" required className="glass-input" value={form.durationH} onChange={(e) => setForm((v) => ({ ...v, durationH: e.target.value }))} placeholder="ex. 14" />
            </div>
            <div>
              <label className="glass-label">Prix / personne (€) *</label>
              <input type="number" min="1" required className="glass-input" value={form.price} onChange={(e) => setForm((v) => ({ ...v, price: e.target.value }))} placeholder="ex. 350" />
            </div>
            <div>
              <label className="glass-label">Nombre max de participants</label>
              <input type="number" min="1" className="glass-input" value={form.maxPax} onChange={(e) => setForm((v) => ({ ...v, maxPax: e.target.value }))} placeholder="Illimité si vide" />
            </div>
            <div className="col-span-2">
              <label className="glass-label">Description *</label>
              <textarea required className="glass-input min-h-[100px] resize-y" value={form.description} onChange={(e) => setForm((v) => ({ ...v, description: e.target.value }))} placeholder="Objectifs pédagogiques, programme, prérequis…" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <input type="checkbox" id="isRemote" checked={form.isRemote} onChange={(e) => setForm((v) => ({ ...v, isRemote: e.target.checked }))} className="w-4 h-4 accent-[#6E7FFF]" />
                <label htmlFor="isRemote" className="text-text-secondary text-sm cursor-pointer">Formation à distance</label>
              </div>
              {!form.isRemote && (
                <>
                  <label className="glass-label">Ville</label>
                  <input className="glass-input" value={form.city} onChange={(e) => setForm((v) => ({ ...v, city: e.target.value }))} placeholder="ex. Lyon" />
                </>
              )}
            </div>
            <div>
              <label className="glass-label">Dates de session</label>
              <div className="flex gap-2 mb-2">
                <input type="date" className="glass-input flex-1" value={form.newDate} onChange={(e) => setForm((v) => ({ ...v, newDate: e.target.value }))} />
                <button type="button" onClick={addDate} className="glass-btn px-3 py-2 rounded-xl text-sm">+</button>
              </div>
              {dates.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {dates.map((d) => (
                    <span key={d} className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-accent/10 border border-accent/20 text-accent text-xs">
                      {new Date(d).toLocaleDateString('fr-FR')}
                      <button type="button" onClick={() => setDates((v) => v.filter((x) => x !== d))} className="text-accent/60 hover:text-accent leading-none">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {error && <p className="text-danger text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">{saving ? 'Publication…' : 'Publier'}</button>
            <button type="button" onClick={() => { setAdding(false); setDates([]) }} className="glass-btn px-4 py-2.5 text-sm rounded-xl">Annuler</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="glass-card h-24 animate-pulse" />)}</div>
      ) : offers.length === 0 ? (
        <div className="glass-card p-14 text-center">
          <p className="text-5xl mb-4">🎓</p>
          <p className="text-text-primary font-semibold mb-2">Aucune formation publiée</p>
          <p className="text-text-secondary text-sm">Créez votre première offre pour apparaître dans le catalogue public.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => {
            const isExpanded = expanded === offer.id
            return (
              <div key={offer.id} className="glass-card overflow-hidden">
                <div className="p-5 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-xs px-2.5 py-0.5 rounded-full border ${STATUS_COLOR[offer.status]}`}>{STATUS_LABEL[offer.status]}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.10] text-text-muted">{offer.topic}</span>
                      {offer.isRemote && <span className="text-xs text-text-muted">🖥 Distanciel</span>}
                    </div>
                    <h3 className="text-text-primary font-semibold">{offer.title}</h3>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-text-muted">
                      <span>⏱ {offer.durationH}h</span>
                      <span>💰 {fmt(offer.price)}/pers.</span>
                      {offer.maxPax && <span>👥 {offer._count.registrations}/{offer.maxPax}</span>}
                      {!offer.maxPax && <span>👥 {offer._count.registrations} inscrit{offer._count.registrations !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setExpanded(isExpanded ? null : offer.id)} className="glass-btn px-3 py-2 text-xs rounded-xl">
                      {isExpanded ? 'Fermer' : 'Gérer'}
                    </button>
                    <button onClick={() => toggleStatus(offer)} className={`glass-btn px-3 py-2 text-xs rounded-xl ${offer.status === 'ACTIVE' ? 'hover:text-danger hover:border-danger/30' : 'hover:text-success hover:border-success/30'}`}>
                      {offer.status === 'ACTIVE' ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-white/[0.08] p-5 space-y-5">
                    {/* Dates */}
                    <div>
                      <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Sessions planifiées</h4>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {offer.dates.length === 0 && <p className="text-text-muted text-xs">Aucune date planifiée.</p>}
                        {offer.dates.map((d) => (
                          <span key={d.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-accent/10 border border-accent/20 text-accent text-xs">
                            {new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                            <button onClick={() => removeDate(d.id)} className="text-accent/50 hover:text-danger leading-none ml-1">×</button>
                          </span>
                        ))}
                      </div>
                      <AddDateRow onAdd={(date) => addOfferDate(offer.id, date)} />
                    </div>

                    {/* Inscriptions */}
                    <div>
                      <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                        Inscriptions ({offer.registrations.length})
                      </h4>
                      {offer.registrations.length === 0 ? (
                        <p className="text-text-muted text-xs">Aucune inscription pour l'instant.</p>
                      ) : (
                        <div className="space-y-2">
                          {offer.registrations.map((r) => (
                            <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.07]">
                              <div className="flex-1 min-w-0">
                                <p className="text-text-primary text-sm font-medium">{r.firstName} {r.lastName}</p>
                                <p className="text-text-muted text-xs">{r.email}{r.phone ? ` · ${r.phone}` : ''}{r.company ? ` · ${r.company.name}` : ''}</p>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${REG_COLOR[r.status]}`}>
                                {r.status === 'PENDING' ? 'En attente' : r.status === 'CONFIRMED' ? 'Confirmé' : 'Annulé'}
                              </span>
                              {r.status === 'PENDING' && (
                                <button onClick={() => confirmRegistration(r.id)} className="glass-btn px-3 py-1.5 text-xs rounded-lg bg-success/10 border-success/30 text-success hover:bg-success/20 flex-shrink-0">
                                  Confirmer
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function AddDateRow({ onAdd }: { onAdd: (date: string) => void }) {
  const [val, setVal] = useState('')
  return (
    <div className="flex gap-2">
      <input type="date" className="glass-input text-sm flex-1" value={val} onChange={(e) => setVal(e.target.value)} />
      <button
        type="button"
        onClick={() => { if (val) { onAdd(val); setVal('') } }}
        className="glass-btn px-4 py-2 text-xs rounded-xl"
      >
        + Ajouter
      </button>
    </div>
  )
}
